import API from "Services/Api";
import { createFilterParams } from "Utils/searchFilters";

type Date = {
  date: string;
};

export type CreateSchedule = {
  datetime: string;
  firstName: string;
  lastName: string;
  address: {
    zipcode: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  phone: string;
  email: string;
  patients: {
    fullName: string;
    ddd: string;
    phone: string;
    email: string;
    cpf: string;
    birthDate: string;
    weight: string;
    height: string;
    gender: string;
    mothersFullName: string;
    medicalClaimsFiles?: { id: string }[];
  }[];
};

type FileAPI = {
  filename: string;
  id: string;
};

type PreOrderDate = {
  cep: string;
  month: string;
  appointmentType?: number | null;
  preOrderId: number;
  clientId: number;
};

type PreOrderHours = Omit<PreOrderDate, "month"> & {
  date: string;
};

export const getPartnersDatesAPI = async (
  cep: string,
  month: string,
  appointmentType?: number | null
): Promise<Date[]> => {
  const response = await API.get(
    `/appointments/v2/dates/${cep}/${month}${
      appointmentType ? `/${appointmentType}` : ""
    }`
  );
  return response.data;
};

export const getPartnersHoursAPI = async (
  cep: string,
  date: string,
  appointmentType?: number | null
): Promise<any> => {
  const response = await API.get(
    `/appointments/v2/times/${cep}/${date}${
      appointmentType ? `/${appointmentType}` : ""
    }`
  );
  return response.data;
};

export const getPreOrderDatesAPI = async ({
  cep,
  month,
  appointmentType,
  clientId,
  preOrderId,
}: PreOrderDate): Promise<Date[]> => {
  try {
    const response = await API.get(
      `/appointments/v2/dates/${cep}/${month}${
        appointmentType ? `/${appointmentType}` : ""
      }`,
      {
        params: {
          client_id: clientId,
          pre_order_id: preOrderId,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw error?.response?.data?.error || "";
  }
};

export const getPreOrderHoursAPI = async ({
  cep,
  date,
  appointmentType,
  clientId,
  preOrderId,
}: PreOrderHours): Promise<any> => {
  const response = await API.get(
    `/appointments/v2/times/${cep}/${date}${
      appointmentType ? `/${appointmentType}` : ""
    }`,
    {
      params: {
        client_id: clientId,
        pre_order_id: preOrderId,
      },
    }
  );
  return response.data;
};

interface Genders {
  gender: string;
  sub_gender: string;
}

interface Appointments {
  id: number;
  name: string;
}

interface Combos {
  appointments: Appointments[];
  genders: Genders[];
}

export const getPartnersCombos = async (): Promise<Combos> => {
  const combos = await API.get(`/appointments/v2/combos`);
  return combos.data;
};

export const createScheduleAPI = async (data: { data: CreateSchedule }) => {
  const response = await API.post(`/appointments/v2/create`, {
    ...data.data,
  });
  return response.data;
};

export const uploadFilesPartners = async (data: FormData): Promise<FileAPI> => {
  const response = await API.post(`/appointments/v2/upload`, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

interface ScheduleListParams {
  date?: string;
  search?: string;
  page: number;
}

export type ScheduleListAPIData = {
  acuity_id: number;
  appointment_at: string;
  created_at: string;
  email: string;
  full_name: string;
  id: number;
  phone: string;
};
interface ScheduleListAPI {
  current_page: number;
  data: ScheduleListAPIData[];
  last_page: number;
  total: number;
}

export const getSchedulesListAPI = async (
  params: ScheduleListParams
): Promise<ScheduleListAPI> => {
  const response = await API.get(`/appointments/v2`, {
    params: createFilterParams(params),
  });
  return response.data;
};

interface ScheduleDetailsAPI {
  id: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  date: string;
  time: string;
  endTime: string;
  dateCreated: string;
  datetimeCreated: string;
  datetime: string;
  price: string;
  priceSold: string;
  paid: string;
  amountPaid: string;
  type: string;
  appointmentTypeID: number;
  classID: null;
  addonIDs: [];
  category: string;
  duration: string;
  calendar: string;
  calendarID: number;
  certificate: null;
  confirmationPage: string;
  confirmationPagePaymentLink: string;
  location: string;
  notes: string;
  timezone: string;
  calendarTimezone: string;
  canceled: boolean;
  canClientCancel: boolean;
  canClientReschedule: boolean;
  labels: null;
  forms: {
    id: number;
    name: string;
    values: {
      id: number;
      fieldID: number;
      value: string;
      name: string;
    }[];
  }[];
  formsText: string;
  isVerified: boolean;
  scheduledBy: string;
}

export const getSchedulesDetailsAPI = async (
  id: number
): Promise<ScheduleDetailsAPI> => {
  const response = await API.get(`/appointments/v2/show/${id}`);
  return response.data;
};

export type RescheduleAppointment = {
  id: number;
  appointment: string;
};

type ReschedulePreOrderAppointment = RescheduleAppointment & {
  client_id: number;
  preOrderId?: number;
  acuity_id?: number;
};

export const reschedulePartperAPI = async (
  data: RescheduleAppointment
): Promise<void> => {
  const { appointment, id } = data;
  const response = await API.put(`/appointments/v2/${id}`, {
    datetime: appointment,
  });
  return response.data;
};

export const rescheduleV2 = async (
  data: Partial<ReschedulePreOrderAppointment>
): Promise<void> => {
  const { appointment, id, client_id, preOrderId } = data;
  const response = await API.put(`/appointments/v2/${id}`, {
    datetime: appointment,
    client_id,
    ...(preOrderId && { preOrderId }),
  });
  return response.data;
};

export const cancelPartperAPI = async (id: number): Promise<void> => {
  const response = await API.delete(`/appointments/v2/${id}`);
  return response.data;
};

export const validateCepPartner = async (
  cep: string
): Promise<{ message: string }> => {
  const response = await API.get(`/appointments/v2/${cep}`);
  return response.data;
};
